import { useEffect, useMemo, useRef, useState } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import SendIcon from "@mui/icons-material/Send";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import NoDataImage from "assets/images/no-data2.svg";
import AfterVisitSummaryEditor from "features/medical-scribe/after-visit-summary/after-visit-summary-editor.component";
import { getAfterVisitSummaryHtml } from "features/medical-scribe/after-visit-summary/after-visit-summary-html.util";
import AfterVisitSummaryPrintDialog from "features/medical-scribe/after-visit-summary/after-visit-summary-print-dialog.component";
import SentLetterPreviewDialog from "features/medical-scribe/after-visit-summary/sent-letter-preview-dialog.component";
import useAfterVisitSummaryEmail from "features/medical-scribe/after-visit-summary/use-after-visit-summary-email.hook";
import { getScribeNoteErrorDisplay } from "features/medical-scribe/components/scribe-note-error.util";
import ScribePromptCollapsible from "features/medical-scribe/components/scribe-prompt-collapsible.component";
import useLatestScribeNote from "features/medical-scribe/hooks/use-latest-scribe-note.hook";
import {
  getScribeModalNestedDialogZIndex,
  getScribeModalPopoverZIndex
} from "features/medical-scribe/medical-scribe-constants";
import MarkdownContainer from "features/patients/sections/patient-ai-summary/components/markdown-container.component";
import useCopyHtmlContentToClipboard from "features/patients/sections/patient-chart-notes/hooks/use-copy-html-content-to-clipboard.hook";
import {
  AMBIENT_SCRIBE_NOTE_PARENT_TYPE,
  AMBIENT_SCRIBE_NOTE_STATUS
} from "services/types/ambient-scribe-note.type";

import AfterVisitSummaryGenerateSection from "../after-visit-summary-generate-section.component";

type AfterVisitSummaryTabProps = {
  scribeId: number;
  patientId: number | undefined;
  /** Union of the local latch and `is_generating_after_visit_summary` from the scribe. */
  isGenerating: boolean;
  onGenerateStarted: () => void;
};

/**
 * The After Visit Summary tab in the scribe details dialog (Intercom 215475744511616) — the
 * patient-friendly letter generated from this recording. Sibling of `AudiologySummaryTab`: the
 * letter lives only in `ai_ambientscribe_notes` (`parent_type: AfterVisitSummary`), so this reads
 * the notes endpoint and drives its spinner off `is_generating_after_visit_summary`.
 *
 * Extra over the audiology tab: **Email to Patient**, which opens the shared email form prefilled
 * with the letter and the approved subject ("Your After Visit Summary from {clinic}").
 */
export default function AfterVisitSummaryTab({
  scribeId,
  patientId,
  isGenerating,
  onGenerateStarted
}: Readonly<AfterVisitSummaryTabProps>) {
  const { newest, latestSucceeded, prompt, isFetching, refetch } =
    useLatestScribeNote({
      scribeId,
      parentType: AMBIENT_SCRIBE_NOTE_PARENT_TYPE.AFTER_VISIT_SUMMARY,
      enabled: true,
      // Opt in to `sent_email_id`: this surface is behind the `afterVisitSummary` flag, which is
      // what keeps the field off the shared projection the audiology tab uses.
      withSentEmail: true,
      withPrompt: true
    });

  // The generation result never touches the scribe, so the progress flag going false is the
  // "a row now exists" signal — the note WS notification only shortens the wait to the next poll.
  const wasGeneratingRef = useRef(isGenerating);
  useEffect(() => {
    if (wasGeneratingRef.current && !isGenerating) {
      refetch();
    }
    wasGeneratingRef.current = isGenerating;
  }, [isGenerating, refetch]);

  const [showCopied, setShowCopied] = useState(false);
  const copyHtmlContent = useCopyHtmlContentToClipboard();

  const { patient, isPatientLoaded, canEmail, cannotEmailReason, openEmail } =
    useAfterVisitSummaryEmail({
      scribeId,
      patientId,
      enabled: true
    });

  const theme = useTheme();
  const [previewEmailId, setPreviewEmailId] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isPrintOpen, setIsPrintOpen] = useState(false);
  const nestedDialogZIndex = getScribeModalNestedDialogZIndex(
    theme.zIndex.snackbar
  );
  // The scribe modal sits above MUI's tooltip layer, so tooltips need the popover layer too.
  const tooltipPopperProps = useMemo(
    () => ({
      style: { zIndex: getScribeModalPopoverZIndex(theme.zIndex.snackbar) }
    }),
    [theme.zIndex.snackbar]
  );
  // A PATCH resolves before the invalidated read lands, and the hook keeps returning the OLD letter
  // until it does — so nothing may act on the letter in that window.
  const isSaveInFlight = isFetching;

  // Three states (see `AudiologySummaryTab`): `undefined` = untouched, `null` = "resolve like the
  // automatic run", a number = an explicit template.
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    number | null | undefined
  >(undefined);
  const effectiveTemplateId =
    selectedTemplateId === undefined
      ? (latestSucceeded?.ambient_scribe_template_id ?? null)
      : selectedTemplateId;

  // Normalized AND merge-tag resolved — a practice-edited template can return markdown, and the
  // greeting is a literal `{{Patient.FirstName}}` the backend never expands. See
  // `getAfterVisitSummaryHtml`.
  const letterHtml = getAfterVisitSummaryHtml(latestSucceeded?.notes, patient);
  const sentEmailId = latestSucceeded?.sent_email_id ?? null;
  const hasLetter = Boolean(letterHtml);
  const failure =
    newest?.status === AMBIENT_SCRIBE_NOTE_STATUS.FAILED ? newest : undefined;
  const failureDisplay = failure
    ? getScribeNoteErrorDisplay(
        failure.error,
        AMBIENT_SCRIBE_NOTE_PARENT_TYPE.AFTER_VISIT_SUMMARY
      )
    : undefined;

  const handleCopy = async () => {
    try {
      await copyHtmlContent(letterHtml);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy the After Visit Summary:", err);
    }
  };

  if (isGenerating) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        spacing={2}
        py={6}
        mt={2}
      >
        <CircularProgress size={32} />
        <Typography variant="body3" color="text.secondary">
          Generating After Visit Summary…
        </Typography>
        <Typography variant="body1" color="text.secondary">
          This may take a moment. The letter will appear here automatically.
        </Typography>
      </Stack>
    );
  }

  if (!hasLetter && !failure) {
    return (
      <Stack
        direction={{ xs: "column", sm: "row" }}
        columnGap={2.5}
        alignItems="center"
        justifyContent="center"
        width="100%"
        minHeight="280px"
        mt={2}
      >
        <Box
          component="img"
          src={NoDataImage}
          alt=""
          sx={{ height: "130px", p: 2 }}
        />
        <Stack rowGap={3}>
          <Typography variant="body3" mb={3}>
            No After Visit Summary generated yet. Generate a patient-friendly
            letter from this recording.
          </Typography>
          <AfterVisitSummaryGenerateSection
            scribeId={scribeId}
            isGenerating={isGenerating}
            hasExistingSummary={false}
            selectedTemplateId={effectiveTemplateId}
            onSelectedTemplateIdChange={setSelectedTemplateId}
            onGenerateStarted={onGenerateStarted}
          />
        </Stack>
      </Stack>
    );
  }

  return (
    <Box width="100%" mt={2}>
      {/* A failure and a letter can BOTH be present — never hide the older good letter. */}
      {failure && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          data-testid="after-visit-summary-error"
        >
          {failureDisplay?.message}
          <ScribePromptCollapsible
            prompt={failureDisplay?.detail}
            label="error details"
            testId="scribe-note-error-detail"
            toggleTestId="scribe-note-error-detail-toggle"
          />
        </Alert>
      )}

      {hasLetter && !isEditing && (
        <Stack direction="row" alignItems="center" gap={1} mb={2}>
          <Tooltip
            title="Copy letter"
            arrow
            disableInteractive
            PopperProps={tooltipPopperProps}
          >
            <IconButton
              onClick={handleCopy}
              color="primary"
              sx={{ border: "1px solid", borderColor: "divider" }}
              data-testid="avs-tab-copy"
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {showCopied && (
            <Typography variant="caption" color="success.main">
              Copied!
            </Typography>
          )}
          <Stack direction="row" flex={1} justifyContent="flex-end">
            {sentEmailId != null && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPreviewEmailId(sentEmailId)}
                data-testid="avs-tab-view-sent-email"
                sx={{ mr: 1 }}
              >
                View sent email
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon sx={{ width: 16 }} />}
              onClick={() => setIsEditing(true)}
              disabled={isSaveInFlight}
              data-testid="avs-tab-edit"
              sx={{ mr: 1 }}
            >
              Edit
            </Button>
            {/* Gated on a LOADED patient, not a truthy one: the print header renders the patient
                block from this object, and an empty one prints blank labels. */}
            <Tooltip
              title={isPatientLoaded ? "" : "Loading the patient’s details…"}
              PopperProps={tooltipPopperProps}
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PrintIcon sx={{ width: 16 }} />}
                  onClick={() => setIsPrintOpen(true)}
                  disabled={!isPatientLoaded || isSaveInFlight}
                  data-testid="avs-tab-print"
                  sx={{ mr: 1 }}
                >
                  Print
                </Button>
              </span>
            </Tooltip>
            {/* `fromScribeModal` is what lifts the email form above this dialog (1501); without it
                MUI's default 1300 puts the form underneath and the user cannot reach it. */}
            <Tooltip title={cannotEmailReason} PopperProps={tooltipPopperProps}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SendIcon sx={{ width: 16 }} />}
                  onClick={() =>
                    openEmail(letterHtml, {
                      fromScribeModal: true,
                      noteId: latestSucceeded?.id
                    })
                  }
                  disabled={!canEmail || isSaveInFlight}
                  data-testid="avs-tab-email-to-patient"
                >
                  {sentEmailId != null ? "Email again" : "Email to Patient"}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      )}

      {!isEditing && (
        <Stack direction="row" justifyContent="center" mb={2}>
          <AfterVisitSummaryGenerateSection
            scribeId={scribeId}
            isGenerating={isGenerating}
            hasExistingSummary={hasLetter}
            selectedTemplateId={effectiveTemplateId}
            onSelectedTemplateIdChange={setSelectedTemplateId}
            onGenerateStarted={onGenerateStarted}
          />
        </Stack>
      )}

      {hasLetter &&
        (isEditing && latestSucceeded ? (
          /* Mounted only while editing, which is what makes the editor's mount-time seed safe —
             see the note in `AfterVisitSummaryEditor`. */
          <AfterVisitSummaryEditor
            noteId={latestSucceeded.id}
            letterHtml={letterHtml}
            onDone={() => setIsEditing(false)}
            nestedDialogZIndex={nestedDialogZIndex}
          />
        ) : (
          /* `html` is unconditional now: `getAfterVisitSummaryHtml` above has already converted a
             markdown letter, so the `isLikelyHtml` probe that used to sit here could no longer
             ever be false. */
          <MarkdownContainer html>{letterHtml}</MarkdownContainer>
        ))}

      <ScribePromptCollapsible prompt={prompt} />

      {/* Nested inside the scribe modal, so it needs the same +112 layer the email form does —
          otherwise it paints behind at MUI's default 1300. Mounted only while requested, which
          keeps `keepUnusedDataFor: 0` minting fresh attachment URLs. */}
      {previewEmailId != null && (
        <SentLetterPreviewDialog
          emailId={previewEmailId}
          onClose={() => setPreviewEmailId(null)}
          zIndex={nestedDialogZIndex}
        />
      )}

      {/* Same layer, and mounted only while requested so each open builds a fresh blob. */}
      {isPrintOpen && (
        <AfterVisitSummaryPrintDialog
          letterHtml={letterHtml}
          patient={patient}
          onClose={() => setIsPrintOpen(false)}
          zIndex={nestedDialogZIndex}
        />
      )}
    </Box>
  );
}
